import { works as catalogWorks } from "@/lib/catalog";
import { normalizeText } from "@/lib/normalize";
import { parseReleaseTime } from "@/lib/releaseDate";
import { getWorkGenres } from "@/lib/taxonomy";
import { getWorkRefKey } from "@/lib/workIdentity";
import type { Work } from "@/types/work";

/**
 * 検索用の索引。
 *
 * 目的：
 * - 作品ID → 作品 の Map 索引（O(1) 解決）
 * - 正規化済み文字列のキャッシュ（クエリのたびに再正規化しない）
 * - 発売日・追加日・更新日のタイムスタンプ事前計算（ソートで Date をパースしない）
 *
 * 本番（数十万件）ではこの索引をブラウザに持たせず、サーバー側の検索エンジンが担当する。
 * ここはデモデータ用の実装であり、UIからは CatalogRepository 経由でしか触らない。
 */

export interface FieldCandidate {
  value: string;
  weight: number;
}

export interface IndexedWork {
  work: Work;
  /** 小文字化した作品ID（大小文字を無視した一致判定用） */
  idKey: string;
  normalizedTitle: string;
  normalizedAlternatives: string[];
  candidates: FieldCandidate[];
  releaseTime: number;
  addedTime: number;
  updatedTime: number;
  priceValue: number;
  rankingValue: number | null;
  ratingValue: number | null;
  reviewValue: number;
  genreIdSet: Set<string>;
  audienceIdSet: Set<string>;
}

function toTime(value: string | null | undefined): number {
  if (!value) return 0;
  const time = Date.parse(value);
  return Number.isNaN(time) ? 0 : time;
}

/** 作品1件分の検索候補（フィールド重み付き）を組み立てる */
export function buildFieldCandidates(work: Work): FieldCandidate[] {
  const candidates: FieldCandidate[] = [
    { value: normalizeText(work.title), weight: 900 },
    { value: normalizeText(work.id), weight: 350 },
    { value: normalizeText(work.providerId), weight: 300 },
    { value: normalizeText(work.creator), weight: 520 },
    { value: normalizeText(work.maker), weight: 320 },
  ];
  work.alternativeTitles.forEach((title) => candidates.push({ value: normalizeText(title), weight: 700 }));
  work.performers.forEach((performer) => candidates.push({ value: normalizeText(performer), weight: 430 }));
  if (work.series) candidates.push({ value: normalizeText(work.series), weight: 340 });
  getWorkGenres(work).forEach((genre) => {
    candidates.push({ value: normalizeText(genre.name), weight: 340 });
    candidates.push({ value: normalizeText(genre.kana), weight: 320 });
    genre.aliases.forEach((alias) => candidates.push({ value: normalizeText(alias), weight: 320 }));
    candidates.push({ value: normalizeText(genre.providerGenreId), weight: 280 });
  });
  work.tags.forEach((tag) => candidates.push({ value: normalizeText(tag), weight: 300 }));
  work.searchKeywords.forEach((keyword) => candidates.push({ value: normalizeText(keyword), weight: 210 }));
  candidates.push({ value: normalizeText(work.description), weight: 80 });
  return candidates;
}

function buildIndexedWork(work: Work): IndexedWork {
  return {
    work,
    idKey: work.id.toLowerCase(),
    normalizedTitle: normalizeText(work.title),
    normalizedAlternatives: work.alternativeTitles.map((title) => normalizeText(title)),
    candidates: buildFieldCandidates(work),
    releaseTime: parseReleaseTime(work.releaseDate),
    addedTime: toTime(work.addedAt),
    updatedTime: toTime(work.updatedAt),
    priceValue: work.price,
    rankingValue: work.rankingScore,
    ratingValue: work.rating,
    reviewValue: work.reviewCount ?? 0,
    genreIdSet: new Set(work.genreIds),
    audienceIdSet: new Set(work.audienceIds),
  };
}

let workIndex: Map<string, IndexedWork> | null = null;
let idIndex: Map<string, Work> | null = null;
let refIndex: Map<string, Work> | null = null;

export function getWorkIndex(): Map<string, IndexedWork> {
  if (!workIndex) {
    const next = new Map<string, IndexedWork>();
    catalogWorks.forEach((work) => {
      next.set(work.id.toLowerCase(), buildIndexedWork(work));
    });
    workIndex = next;
  }
  return workIndex;
}

/** 作品IDの索引（大小文字を無視） */
export function getIdIndex(): Map<string, Work> {
  if (!idIndex) {
    const next = new Map<string, Work>();
    catalogWorks.forEach((work) => next.set(work.id.toLowerCase(), work));
    idIndex = next;
  }
  return idIndex;
}

export function getIndexedWork(id: string): IndexedWork | undefined {
  if (!id) return undefined;
  return getWorkIndex().get(id.toLowerCase());
}

/** 提供元の複合キー（providerKey + serviceId + providerId）で作品を解決する */
export function getWorkByRefKey(refKey: string): Work | undefined {
  if (!refKey) return undefined;
  if (!refIndex) {
    const next = new Map<string, Work>();
    catalogWorks.forEach((work) => next.set(getWorkRefKey(work), work));
    refIndex = next;
  }
  return refIndex.get(refKey);
}

export function getCandidatesFor(work: Work): FieldCandidate[] {
  const indexed = getIndexedWork(work.id);
  if (indexed) return indexed.candidates;
  // 索引に存在しない作品（将来の外部データ）でも落ちないようにする
  return buildFieldCandidates(work);
}

export interface WorkTimes {
  release: number;
  added: number;
  updated: number;
}

export function getWorkTimes(work: Work): WorkTimes {
  const indexed = getIndexedWork(work.id);
  if (indexed) {
    return { release: indexed.releaseTime, added: indexed.addedTime, updated: indexed.updatedTime };
  }
  return {
    release: parseReleaseTime(work.releaseDate),
    added: toTime(work.addedAt),
    updated: toTime(work.updatedAt),
  };
}